// src/pages/Actiecentrum.js
// vACT-021
// - Documentsetacties voor Personeel openen rechtstreeks het juiste personeelslid en de Documentsets-popup
// vACT-016
// - Documentsetacties kunnen rechtstreeks terug naar de gekoppelde Plaatsing
// - Klik op de Actie-kolom opent bij een Plaatsing/documentset de juiste bron; documentset opent meteen de popup
// vACT-015
// - Kolomvolgorde aangepast: Urgentie, Deadline, Bron, Actie, Bedrijfscode, Docs, Status, Relatie, Personeel
// - Relatie en Personeel toegevoegd met sortering en selectie
// - Bedrijfscode en Status iets smaller
// vACT-014
// - 100% StaffIQ-basislayout gelijkgetrokken met Personeel.js
// - Rechterkolom is altijd volledig zichtbaar; bij openen/Vernieuw is deze leeg en vergrendeld
// - Werkbalk uitgebreid met de standaardknop Spreek
// - Omschrijving-editor werkt als Opmerking-editor in Personeel: in de rechterkolom met Terug
// - Labels, invoervelden, lettergroottes, kleuren, marges en knoppen volgen Personeel.js
// - Nieuwe actie en geselecteerde actie blijven in dezelfde vaste rechterkolom
// - Bestaande acties kunnen nu worden gewijzigd en opgeslagen
// - Handmatige acties kunnen worden verwijderd na bevestiging
// - Automatisch gegenereerde acties blijven beschermd tegen verwijderen
// - Verwijderen gecorrigeerd: DELETE stuurt nu een lege JSON-body mee omdat de standaardheaders Content-Type application/json bevatten
// - Bronkoppeling gebruikersvriendelijk gemaakt: Relatie, Personeel, Plaatsing of Overig
// - Bronkoppeling uitgebreid met Aanvraag, Vacature, Offerte/Opdracht, Factuur en Notitie/Taak
// - Bronlabels worden per module opgebouwd uit herkenbare nummers, titels en relatienamen
// - Technische source_type/source_id/source_label worden automatisch gevuld
// - Urgentie gelijkgetrokken met Mijn Actiesignalen: Info, Normaal, Hoog en Spoed
// - Bestaande waarden warning/urgent blijven compatibel en worden als Hoog/Spoed getoond
// - Bestaande backend/API-functionaliteit blijft verder ongewijzigd

import React, { useEffect, useMemo, useRef, useState } from "react";
import useVoiceCommands from "../components/useVoiceCommands";
import HelpButton from "../components/HelpButton";

const VERSION = "vACT-023";

/* ============================================================
   ALGEMENE HULPFUNCTIES
   ============================================================ */

function getHeaders() {
  const token =
    sessionStorage.getItem("token") ||
    localStorage.getItem("token") ||
    "";

  const tenant =
    sessionStorage.getItem("tenant") ||
    localStorage.getItem("tenant") ||
    "";

  const companyCodeIds =
    sessionStorage.getItem("selectedCompanyCodeIds") ||
    localStorage.getItem("selectedCompanyCodeIds") ||
    sessionStorage.getItem("company_code_ids") ||
    localStorage.getItem("company_code_ids") ||
    "";

  const headers = {
    "Content-Type": "application/json",
  };

  if (token) headers.Authorization = `Bearer ${token}`;
  if (tenant) headers["x-tenant"] = tenant;
  if (companyCodeIds) headers["x-company-code-ids"] = companyCodeIds;

  return headers;
}

function formatDateNL(value) {
  if (!value) return "";

  const raw = String(value).slice(0, 10);
  const parts = raw.split("-");

  if (parts.length !== 3) return raw;

  const [year, month, day] = parts;
  return `${day}-${month}-${year}`;
}

function normalizeText(value) {
  return String(value || "")
    .toLowerCase()
    .trim();
}

function urgencyLabel(value) {
  const v = normalizeText(value);

  if (v === "spoed" || v === "urgent") return "Spoed";
  if (v === "hoog" || v === "warning") return "Hoog";
  if (v === "normaal") return "Normaal";
  return "Info";
}

function statusLabel(item) {
  if (item?.postponed_until) {
    return `Uitgesteld tot ${formatDateNL(item.postponed_until)}`;
  }

  if (normalizeText(item?.status) === "completed") {
    return "Afgehandeld";
  }

  return "Open";
}

function sourceTypeLabel(value) {
  const v = normalizeText(value);

  if (v === "organisation" || v === "organisatie") return "Relatie";
  if (v === "personnel" || v === "personeel") return "Personeel";
  if (v === "application" || v === "aanvraag") return "Aanvraag";
  if (v === "vacancy" || v === "vacature") return "Vacature";
  if (v === "placement" || v === "plaatsing") return "Plaatsing";
  if (v === "offer" || v === "offerte" || v === "order" || v === "opdracht") {
    return "Offerte/Opdracht";
  }
  if (v === "invoice" || v === "factuur") return "Factuur";
  if (
    v === "note" ||
    v === "task" ||
    v === "note_task" ||
    v === "notitie" ||
    v === "taak"
  ) {
    return "Notitie/Taak";
  }

  return value || "";
}

function dispatchActionCenterChanged() {
  try {
    window.dispatchEvent(
      new CustomEvent("staffiq-action-center-changed")
    );
  } catch {
    // Geen actie nodig wanneer CustomEvent niet beschikbaar is.
  }
}

function emptyForm(companyCodeId = "") {
  return {
    id: null,
    company_code_id: companyCodeId || "",
    title: "",
    description: "",
    due_date: "",
    reminder_days: 7,
    urgency: "warning",
    source_type: "",
    source_id: "",
    source_label: "",
    status: "open",
    postponed_until: "",
    document_count: 0,
  };
}

function itemToForm(item = {}) {
  return {
    id: item?.id ?? null,
    action_type: item?.action_type || "manual",
    company_code_id: String(item?.company_code_id || ""),
    company_code_name: item?.company_code_name || "",
    company_code: item?.company_code || "",
    title: item?.title || "",
    description: item?.description || "",
    due_date: String(item?.due_date || "").slice(0, 10),
    reminder_days: item?.reminder_days ?? 0,
    urgency:
      normalizeText(item?.urgency) === "urgent"
        ? "spoed"
        : normalizeText(item?.urgency) === "warning"
          ? "hoog"
          : item?.urgency || "info",
    source_type: item?.source_type || "",
    source_id: item?.source_id || "",
    source_label: item?.source_label || "",
    source_parent_type: item?.source_parent_type || "",
    source_parent_id: item?.source_parent_id || "",
    status: item?.status || "open",
    postponed_until: String(item?.postponed_until || "").slice(0, 10),
    document_count: item?.document_count || 0,
  };
}

/* ============================================================
   VELDCOMPONENT
   ============================================================ */

function Field({ label, children }) {
  return (
    <>
      <label style={{ fontSize: 13, color: "#333" }}>{label}</label>
      {children}
    </>
  );
}

/* ============================================================
   HOOFDCOMPONENT
   ============================================================ */

export default function Actiecentrum() {
  const [items, setItems] = useState([]);
  const [codes, setCodes] = useState([]);
  const [sourceOptions, setSourceOptions] = useState([]);
  const [sourceBusy, setSourceBusy] = useState(false);

  const [view, setView] = useState("open");
  const [q, setQ] = useState("");

  const [urgencyFilter, setUrgencyFilter] = useState("");
  const [deadlineFilter, setDeadlineFilter] = useState("");
  const [companyCodeFilter, setCompanyCodeFilter] = useState("");
  const [sourceFilter, setSourceFilter] = useState("");
  const [actionFilter, setActionFilter] = useState("");
  const [docsFilter, setDocsFilter] = useState("");
  const [statusFilter, setStatusFilter] = useState("");
  const [relationFilter, setRelationFilter] = useState("");
  const [personnelFilter, setPersonnelFilter] = useState("");

  const [sort, setSort] = useState({ key: "deadline", dir: "asc" });

  const EMPTY_VAL = "__EMPTY__";

  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [message, setMessage] = useState("");

  // mode:
  // search = geen invoer actief
  // new    = nieuwe actie invoeren
  // detail = bestaande actie geselecteerd
  const [mode, setMode] = useState("search");
  const [selectedId, setSelectedId] = useState(null);
  const [form, setForm] = useState(emptyForm());

  const [descriptionEditor, setDescriptionEditor] = useState(false);
  const [speechInfo, setSpeechInfo] = useState("");

  const searchRef = useRef(null);
  const rightScrollRef = useRef(null);

  const isMobile = window.innerWidth <= 768;
  const RIGHT_WIDTH = isMobile ? "100%" : 320;
  const GAP = isMobile ? 18 : 14;
  const TABLE_MIN_WIDTH = 1240;
  const MID_BOTTOM_OFFSET = isMobile ? 110 : 185;

  /* ============================================================
     STIJLEN - GELIJK AAN PLAATSINGEN / STAFFIQ
     ============================================================ */

  const buttonStyle = {
    padding: "7px 12px",
    border: "1px solid #d9d9d9",
    borderRadius: 4,
    background: "white",
    cursor: "pointer",
    whiteSpace: "nowrap",
  };

  const buttonDisabledStyle = {
    ...buttonStyle,
    opacity: 0.55,
    cursor: "not-allowed",
  };

  const primaryButtonStyle = {
    ...buttonStyle,
    background: "#2f7dd1",
    color: "white",
    border: "1px solid rgba(0,0,0,0.05)",
  };

  const successButtonStyle = {
    ...buttonStyle,
    background: "#198754",
    color: "white",
    border: "1px solid rgba(0,0,0,0.05)",
  };

  const dangerButtonStyle = {
    ...buttonStyle,
    background: "#d64545",
    color: "white",
    border: "1px solid rgba(0,0,0,0.05)",
  };

  const neutralButtonStyle = {
    ...buttonStyle,
    background: "#6c757d",
    color: "white",
    border: "1px solid rgba(0,0,0,0.05)",
  };

  const tabStyle = (active) => ({
    ...buttonStyle,
    background: active ? "#2f7dd1" : "white",
    color: active ? "white" : "#333",
    fontWeight: active ? 700 : 400,
  });

  const searchInputStyle = {
    padding: "7px 10px",
    width: isMobile ? "100%" : 220,
    border: "1px solid #d9d9d9",
    borderRadius: 4,
    background: "white",
    outline: "none",
    boxSizing: "border-box",
  };

  const inputStyle = {
    display: "block",
    padding: "7px 10px",
    width: "100%",
    maxWidth: "100%",
    minWidth: 0,
    border: "1px solid #d9d9d9",
    borderRadius: 4,
    background: "white",
    boxSizing: "border-box",
  };

  const selectStyle = {
    display: "block",
    height: 30,
    padding: "0 8px",
    border: "1px solid #d9d9d9",
    borderRadius: 4,
    background: "white",
    width: "100%",
    maxWidth: "100%",
    minWidth: 0,
    boxSizing: "border-box",
  };

  const labelStyle = { fontSize: 13, color: "#333" };

  const sectionTitleStyle = {
    fontSize: 13,
    fontWeight: 700,
    opacity: 0.75,
    marginTop: 8,
  };

  const thBase = {
    textAlign: "left",
    fontSize: 12,
    color: "#444",
    fontWeight: 700,
    padding: "8px 6px",
    borderBottom: "1px solid #e6e6e6",
    whiteSpace: "nowrap",
    position: "sticky",
    top: 0,
    zIndex: 5,
    background: "#fff",
    userSelect: "none",
  };

  const thClickable = { ...thBase, cursor: "pointer" };

  const filterThStyle = {
    ...thBase,
    fontWeight: 400,
    padding: "6px 6px 8px 6px",
    borderBottom: "1px solid #ededed",
    top: 32,
    zIndex: 4,
    cursor: "default",
  };

  const tdStyle = {
    padding: "7px 6px",
    borderBottom: "1px solid #efefef",
    fontSize: 13,
    color: "#222",
    whiteSpace: "nowrap",
    overflow: "hidden",
    textOverflow: "ellipsis",
  };

  function processButtonStyle(baseStyle, disabled = false) {
    return {
      ...(disabled
        ? {
            ...baseStyle,
            opacity: 0.5,
            cursor: "not-allowed",
          }
        : baseStyle),
      width: "100%",
      minWidth: 0,
      minHeight: 32,
      padding: "6px 6px",
      boxSizing: "border-box",
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      whiteSpace: "nowrap",
      textAlign: "center",
      lineHeight: 1.15,
      fontSize: 12,
      overflow: "hidden",
      textOverflow: "ellipsis",
    };
  }

  /* ============================================================
     DATA LADEN
     ============================================================ */

  async function loadCodes() {
    const response = await fetch("/api/company-scope", {
      headers: getHeaders(),
    });

    const data = await response.json();

    if (!response.ok) {
      throw new Error(
        data?.error || "Bedrijfscodes laden mislukt."
      );
    }

    const activeCodes = (data?.company_codes || []).filter(
      (item) => item?.active !== false
    );

    setCodes(activeCodes);
  }

  function sourceOptionLabel(type, item = {}) {
    if (type === "organisation") {
      return item.search_name || item.name || `Relatie ${item.id}`;
    }

    if (type === "personnel") {
      return (
        item.search_name ||
        [item.last_name, item.first_name].filter(Boolean).join(", ") ||
        `Personeel ${item.id}`
      );
    }

    if (type === "placement") {
      const number = item.placement_number || `Plaatsing ${item.id}`;
      const person = item.personnel_name || "";
      const relation = item.organisation_name || "";
      const extra = [person, relation].filter(Boolean).join(" - ");
      return extra ? `${number} - ${extra}` : number;
    }

    if (type === "application") {
      const number = item.application_number || `Aanvraag ${item.id}`;
      const title = item.title || item.job_title || "";
      const relation = item.organisation_name || "";
      const extra = [relation, title].filter(Boolean).join(" - ");
      return extra ? `${number} - ${extra}` : number;
    }

    if (type === "vacancy") {
      const number = item.vacancy_number || `Vacature ${item.id}`;
      const title = item.title || item.job_title || "";
      const relation = item.organisation_name || "";
      const extra = [relation, title].filter(Boolean).join(" - ");
      return extra ? `${number} - ${extra}` : number;
    }

    if (type === "offer") {
      const number = item.invoice_number || item.offer_number || `Offerte/Opdracht ${item.id}`;
      const status = normalizeText(item.status) === "opdracht" ? "Opdracht" : "Offerte";
      const relation = item.organisation_name || "";
      const reference = item.reference || "";
      const extra = [relation, reference].filter(Boolean).join(" - ");
      return extra ? `${status} ${number} - ${extra}` : `${status} ${number}`;
    }

    if (type === "invoice") {
      const number = item.invoice_number || `Factuur ${item.id}`;
      const relation = item.organisation_name || "";
      return relation ? `${number} - ${relation}` : number;
    }

    if (type === "note_task") {
      const kind = normalizeText(item.type || item.kind) === "task" ? "Taak" : "Notitie";
      const title = item.title || item.subject || `${kind} ${item.id}`;
      const relation = item.organisation_name || "";
      const person = item.personnel_name || "";
      const extra = [relation, person].filter(Boolean).join(" - ");
      return extra ? `${kind}: ${title} - ${extra}` : `${kind}: ${title}`;
    }

    return item.source_label || item.name || String(item.id || "");
  }

  async function loadSourceOptions(sourceType) {
    const type = normalizeText(sourceType);

    const supportedTypes = [
      "organisation",
      "personnel",
      "placement",
      "application",
      "vacancy",
      "offer",
      "invoice",
      "note_task",
    ];

    if (!supportedTypes.includes(type)) {
      setSourceOptions([]);
      return;
    }

    setSourceBusy(true);

    try {
      const urlMap = {
        organisation: "/api/organisations?activeOnly=true",
        personnel: "/api/personnel?active=true",
        placement: "/api/placements?active=true",
        application: "/api/applications?active=1",
        vacancy: "/api/vacancies?active=1",
        offer: "/api/offers",
        invoice: "/api/invoices",
        note_task: "/api/notes-tasks",
      };

      const response = await fetch(urlMap[type], { headers: getHeaders() });
      const data = await response.json();

      if (!response.ok) {
        throw new Error(data?.error || "Bronnen laden mislukt.");
      }

      setSourceOptions(
        (Array.isArray(data) ? data : []).map((item) => ({
          id: String(item.id || ""),
          label: sourceOptionLabel(type, item),
        }))
      );
    } catch (err) {
      setSourceOptions([]);
      setError(err?.message || String(err));
    } finally {
      setSourceBusy(false);
    }
  }

  async function loadItems(targetView = view) {
    setBusy(true);
    setError("");

    try {
      const status =
        targetView === "completed" ? "completed" : "open";

      const response = await fetch(
        `/api/action-center/items?status=${encodeURIComponent(status)}`,
        {
          headers: getHeaders(),
        }
      );

      const data = await response.json();

      if (!response.ok) {
        throw new Error(
          data?.error || "Actiepunten laden mislukt."
        );
      }

      setItems(Array.isArray(data) ? data : []);
      dispatchActionCenterChanged();
    } catch (err) {
      setItems([]);
      setError(err?.message || String(err));
    } finally {
      setBusy(false);
    }
  }

  useEffect(() => {
    loadCodes().catch((err) =>
      setError(err?.message || String(err))
    );

    loadItems("open");

    function onCompanyScopeChanged() {
      loadItems(view);
    }

    window.addEventListener(
      "staffiq-company-scope-changed",
      onCompanyScopeChanged
    );

    return () => {
      window.removeEventListener(
        "staffiq-company-scope-changed",
        onCompanyScopeChanged
      );
    };

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (mode === "search") {
      setSourceOptions([]);
      return;
    }

    loadSourceOptions(form.source_type);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mode, form.source_type]);

  /* ============================================================
     FILTERING / SORTERING
     ============================================================ */

  const baseRows = useMemo(() => {
    let rows = items || [];

    if (view === "postponed") {
      rows = rows.filter((item) => !!item?.postponed_until);
    } else if (view === "open") {
      rows = rows.filter((item) => !item?.postponed_until);
    }

    return rows;
  }, [items, view]);

  const urgencyOptions = useMemo(
    () =>
      Array.from(
        new Set(baseRows.map((item) => urgencyLabel(item?.urgency)).filter(Boolean))
      ).sort((a, b) => a.localeCompare(b, "nl")),
    [baseRows]
  );

  const deadlineOptions = useMemo(
    () =>
      Array.from(
        new Set(baseRows.map((item) => formatDateNL(item?.due_date)).filter(Boolean))
      ).sort((a, b) => a.localeCompare(b, "nl")),
    [baseRows]
  );

  const companyCodeOptions = useMemo(
    () =>
      Array.from(
        new Set(
          baseRows
            .map((item) => item?.company_code_name || item?.company_code || "")
            .filter(Boolean)
        )
      ).sort((a, b) => a.localeCompare(b, "nl")),
    [baseRows]
  );

  const sourceOptionsGrid = useMemo(
    () =>
      Array.from(
        new Set(baseRows.map((item) => item?.source_label || "").filter(Boolean))
      ).sort((a, b) => a.localeCompare(b, "nl")),
    [baseRows]
  );

  const actionOptions = useMemo(
    () =>
      Array.from(
        new Set(baseRows.map((item) => item?.title || "").filter(Boolean))
      ).sort((a, b) => a.localeCompare(b, "nl")),
    [baseRows]
  );

  const docsOptions = useMemo(
    () =>
      Array.from(
        new Set(baseRows.map((item) => String(item?.document_count || 0)))
      ).sort((a, b) => Number(a) - Number(b)),
    [baseRows]
  );

  const statusOptions = useMemo(
    () =>
      Array.from(
        new Set(baseRows.map((item) => statusLabel(item)).filter(Boolean))
      ).sort((a, b) => a.localeCompare(b, "nl")),
    [baseRows]
  );

  const relationOptions = useMemo(
    () =>
      Array.from(
        new Set(baseRows.map((item) => item?.relation_name || "").filter(Boolean))
      ).sort((a, b) => a.localeCompare(b, "nl")),
    [baseRows]
  );

  const personnelOptions = useMemo(
    () =>
      Array.from(
        new Set(baseRows.map((item) => item?.personnel_name || "").filter(Boolean))
      ).sort((a, b) => a.localeCompare(b, "nl")),
    [baseRows]
  );

  const shown = useMemo(() => {
    const query = normalizeText(q);

    return baseRows.filter((item) => {
      const urgency = urgencyLabel(item?.urgency);
      const deadline = formatDateNL(item?.due_date);
      const companyCode = item?.company_code_name || item?.company_code || "";
      const source = item?.source_label || "";
      const action = item?.title || "";
      const docs = String(item?.document_count || 0);
      const status = statusLabel(item);
      const relation = item?.relation_name || "";
      const personnel = item?.personnel_name || "";

      if (
        urgencyFilter &&
        (urgencyFilter === EMPTY_VAL ? !!urgency : urgency !== urgencyFilter)
      ) {
        return false;
      }

      if (
        deadlineFilter &&
        (deadlineFilter === EMPTY_VAL ? !!deadline : deadline !== deadlineFilter)
      ) {
        return false;
      }

      if (
        companyCodeFilter &&
        (companyCodeFilter === EMPTY_VAL
          ? !!companyCode
          : companyCode !== companyCodeFilter)
      ) {
        return false;
      }

      if (
        sourceFilter &&
        (sourceFilter === EMPTY_VAL ? !!source : source !== sourceFilter)
      ) {
        return false;
      }

      if (
        actionFilter &&
        (actionFilter === EMPTY_VAL ? !!action : action !== actionFilter)
      ) {
        return false;
      }

      if (docsFilter && docs !== docsFilter) {
        return false;
      }

      if (
        statusFilter &&
        (statusFilter === EMPTY_VAL ? !!status : status !== statusFilter)
      ) {
        return false;
      }

      if (
        relationFilter &&
        (relationFilter === EMPTY_VAL ? !!relation : relation !== relationFilter)
      ) {
        return false;
      }

      if (
        personnelFilter &&
        (personnelFilter === EMPTY_VAL ? !!personnel : personnel !== personnelFilter)
      ) {
        return false;
      }

      if (!query) return true;

      const text = [
        urgency,
        deadline,
        companyCode,
        sourceTypeLabel(item?.source_type),
        source,
        action,
        item?.description,
        docs,
        status,
        relation,
        personnel,
      ]
        .join(" ")
        .toLowerCase();

      return text.includes(query);
    });
  }, [
    baseRows,
    q,
    urgencyFilter,
    deadlineFilter,
    companyCodeFilter,
    sourceFilter,
    actionFilter,
    docsFilter,
    statusFilter,
    relationFilter,
    personnelFilter,
  ]);

  const sortedShown = useMemo(() => {
    const copy = shown.slice();
    const dirMul = sort.dir === "desc" ? -1 : 1;

    const cmpText = (a, b) => {
      const aa = normalizeText(a);
      const bb = normalizeText(b);
      if (aa === bb) return 0;
      return aa < bb ? -1 : 1;
    };

    const cmpNumber = (a, b) => Number(a || 0) - Number(b || 0);

    copy.sort((a, b) => {
      if (sort.key === "urgency") {
        return dirMul * cmpText(urgencyLabel(a?.urgency), urgencyLabel(b?.urgency));
      }
      if (sort.key === "deadline") {
        return dirMul * cmpText(String(a?.due_date || ""), String(b?.due_date || ""));
      }
      if (sort.key === "company_code") {
        return (
          dirMul *
          cmpText(
            a?.company_code_name || a?.company_code || "",
            b?.company_code_name || b?.company_code || ""
          )
        );
      }
      if (sort.key === "source") {
        return dirMul * cmpText(a?.source_label || "", b?.source_label || "");
      }
      if (sort.key === "action") {
        return dirMul * cmpText(a?.title || "", b?.title || "");
      }
      if (sort.key === "docs") {
        return dirMul * cmpNumber(a?.document_count, b?.document_count);
      }
      if (sort.key === "status") {
        return dirMul * cmpText(statusLabel(a), statusLabel(b));
      }
      if (sort.key === "relation") {
        return dirMul * cmpText(a?.relation_name || "", b?.relation_name || "");
      }
      if (sort.key === "personnel") {
        return dirMul * cmpText(a?.personnel_name || "", b?.personnel_name || "");
      }

      return 0;
    });

    return copy;
  }, [shown, sort]);

  const gridCount = Math.min(sortedShown.length, 9999);

  function sortToggle(key) {
    setSort((current) => {
      if (current.key !== key) return { key, dir: "asc" };
      return { key, dir: current.dir === "asc" ? "desc" : "asc" };
    });
  }

  function sortArrow(key) {
    if (sort.key !== key) return "";
    return sort.dir === "asc" ? " ▲" : " ▼";
  }

  /* ============================================================
     FORMULIER / SELECTIE
     ============================================================ */

  function setField(name, value) {
    setForm((current) => ({
      ...current,
      [name]: value,
    }));
  }

  function clearRightColumn() {
    setSelectedId(null);
    setForm(emptyForm(""));
    setMode("search");
    setDescriptionEditor(false);

    setTimeout(() => {
      try {
        rightScrollRef.current?.scrollTo?.({
          top: 0,
          left: 0,
          behavior: "auto",
        });
      } catch {
        // Geen actie nodig.
      }
    }, 0);
  }

  function newItem() {
    setError("");
    setMessage("");
    setSelectedId(null);

    setForm(
      emptyForm(
        form.company_code_id ||
          String(codes[0]?.id || "")
      )
    );

    setMode("new");

    setTimeout(() => {
      try {
        rightScrollRef.current?.scrollTo?.({
          top: 0,
          left: 0,
          behavior: "auto",
        });
      } catch {
        // Geen actie nodig.
      }

      if (isMobile) {
        rightScrollRef.current?.scrollIntoView?.({
          behavior: "smooth",
          block: "start",
        });
      }
    }, 80);
  }

  function selectItem(item) {
    setError("");
    setMessage("");
    setSelectedId(item?.id ?? null);
    setForm(itemToForm(item));
    setMode("detail");

    setTimeout(() => {
      try {
        rightScrollRef.current?.scrollTo?.({
          top: 0,
          left: 0,
          behavior: "auto",
        });
      } catch {
        // Geen actie nodig.
      }

      if (isMobile) {
        rightScrollRef.current?.scrollIntoView?.({
          behavior: "smooth",
          block: "start",
        });
      }
    }, 80);
  }

  function canOpenLinkedSource(item = {}) {
    const type = normalizeText(item?.source_type);
    const parentType = normalizeText(item?.source_parent_type);

    if (type === "placement" || type === "plaatsing") return !!item?.source_id;
    if (type === "personnel" || type === "personeel") return !!item?.source_id;
    if (type === "application" || type === "aanvraag") return !!item?.source_id;
    if (type === "vacancy" || type === "vacature") return !!item?.source_id;
    if (type === "offer" || type === "offerte") return !!item?.source_id;
    if (type === "order" || type === "opdracht") return !!item?.source_id;
    if (type === "invoice" || type === "factuur") return !!item?.source_id;

    return (
      type === "document_set" &&
      ["placement", "plaatsing", "personnel", "personeel"].includes(parentType) &&
      !!item?.source_parent_id
    );
  }

  function openLinkedSource(item = {}) {
    const type = normalizeText(item?.source_type);
    const parentType = normalizeText(item?.source_parent_type);

    let targetType = type;
    let targetId = String(item?.source_id || "").split(":")[0];
    let reopenDocumentSets = false;

    if (type === "document_set") {
      targetType = parentType;
      targetId = String(item?.source_parent_id || "").split(":")[0];
      reopenDocumentSets = true;
    }

    if (!targetId) return;

    try {
      if (["placement", "plaatsing"].includes(targetType)) {
        sessionStorage.setItem("staffiq_reopen_placement_id", targetId);
        if (reopenDocumentSets) {
          sessionStorage.setItem("staffiq_reopen_placement_documentsets", "1");
        } else {
          sessionStorage.removeItem("staffiq_reopen_placement_documentsets");
        }
        window.location.href = "/plaatsingen";
        return;
      }

      if (["personnel", "personeel"].includes(targetType)) {
        sessionStorage.setItem("staffiq_reopen_personnel_id", targetId);
        if (reopenDocumentSets) {
          sessionStorage.setItem("staffiq_reopen_personnel_documentsets", "1");
        } else {
          sessionStorage.removeItem("staffiq_reopen_personnel_documentsets");
        }
        window.location.href = "/personeel";
        return;
      }

      if (["application", "aanvraag"].includes(targetType)) {
        sessionStorage.setItem("staffiq_reopen_application_id", targetId);
        window.location.href = "/aanvragen";
        return;
      }

      if (["vacancy", "vacature"].includes(targetType)) {
        sessionStorage.setItem("staffiq_reopen_vacancy_id", targetId);
        window.location.href = "/vacatures";
        return;
      }

      if (["offer", "offerte"].includes(targetType)) {
        sessionStorage.setItem("staffiq_reopen_offer_id", targetId);
        window.location.href = "/offertes";
        return;
      }

      if (["order", "opdracht"].includes(targetType)) {
        sessionStorage.setItem("staffiq_reopen_offer_id", targetId);
        window.location.href = "/offertes";
        return;
      }

      if (["invoice", "factuur"].includes(targetType)) {
        sessionStorage.setItem("staffiq_reopen_invoice_id", targetId);
        window.location.href = "/facturen";
        return;
      }
    } catch {}
  }

  async function chooseView(nextView) {
    setView(nextView);
    setMessage("");
    setUrgencyFilter("");
    setDeadlineFilter("");
    setCompanyCodeFilter("");
    setSourceFilter("");
    setActionFilter("");
    setDocsFilter("");
    setStatusFilter("");
    setRelationFilter("");
    setPersonnelFilter("");
    setSort({ key: "deadline", dir: "asc" });
    clearRightColumn();
    await loadItems(nextView);
  }

  /* ============================================================
     OMSCHRIJVING-EDITOR - ZELFDE WERKING ALS PERSONEEL
     ============================================================ */

  function closeDescriptionEditorToSmall() {
    const panel = rightScrollRef.current;
    const panelTop = panel ? panel.scrollTop : 0;

    setDescriptionEditor(false);

    requestAnimationFrame(() => {
      try {
        if (panel) panel.scrollTop = panelTop;
      } catch {}
    });
  }

  function openDescriptionEditor() {
    if (mode === "search") return;

    setDescriptionEditor(true);

    setTimeout(() => {
      try {
        if (rightScrollRef.current) {
          rightScrollRef.current.scrollTop = 0;
        }
      } catch {}
    }, 0);
  }

  /* ============================================================
     SPRAAK - ZELFDE BASIS ALS PERSONEEL
     ============================================================ */

  const voice = useVoiceCommands({
    lang: "nl-NL",
    timeoutMs: 5000,
    onCommand: (cmd) => {
      if (!cmd?.type) return false;

      if (cmd.type === "new") {
        newItem();
        return true;
      }

      if (cmd.type === "refresh") {
        vernieuwList();
        return true;
      }

      if (cmd.type === "save") {
        if (mode === "new") createAction();
        if (mode === "detail") updateAction();
        return mode === "new" || mode === "detail";
      }

      return false;
    },
    onDictation: (spokenText) => {
      const raw = String(spokenText || "").trim();
      if (!raw) return;

      setSpeechInfo(`🎙 ${raw}`);
      setTimeout(() => setSpeechInfo(""), 2000);

      const t = normalizeText(raw);

      if (t === "nieuw") {
        newItem();
        return;
      }

      if (t === "vernieuw" || t === "vernieuwen") {
        vernieuwList();
        return;
      }

      if (t === "opslaan") {
        if (mode === "new") createAction();
        if (mode === "detail") updateAction();
        return;
      }

      if (mode === "search") {
        setQ(raw);
      }
    },
  });

  function showMessage(text) {
    setMessage(text);
    setTimeout(() => setMessage(""), 800);
  }

  /* ============================================================
     ACTIES
     ============================================================ */

  async function createAction() {
    setError("");

    if (!form.company_code_id) {
      setError("Kies een bedrijfscode.");
      return;
    }

    if (!String(form.title || "").trim()) {
      setError("Titel is verplicht.");
      return;
    }

    try {
      const response = await fetch(
        "/api/action-center/items",
        {
          method: "POST",
          headers: getHeaders(),
          body: JSON.stringify({
            company_code_id: Number(form.company_code_id),
            title: form.title,
            description: form.description,
            due_date: form.due_date,
            reminder_days: Number(form.reminder_days || 0),
            urgency: form.urgency,
            source_type: form.source_type,
            source_id: form.source_id,
            source_label: form.source_label,
          }),
        }
      );

      const data = await response.json();

      if (!response.ok) {
        throw new Error(
          data?.error || "Actie aanmaken mislukt."
        );
      }

      showMessage("Opgeslagen ✅");
      setView("open");

      setSelectedId(null);
      setForm(emptyForm(""));
      setMode("search");
      setDescriptionEditor(false);

      await loadItems("open");
      dispatchActionCenterChanged();
    } catch (err) {
      setError(err?.message || String(err));
    }
  }

  async function updateAction() {
    if (mode !== "detail" || !form.id) return;

    setError("");

    if (!form.company_code_id) {
      setError("Kies een bedrijfscode.");
      return;
    }

    if (!String(form.title || "").trim()) {
      setError("Titel is verplicht.");
      return;
    }

    try {
      const response = await fetch(
        `/api/action-center/items/${encodeURIComponent(form.id)}`,
        {
          method: "PUT",
          headers: getHeaders(),
          body: JSON.stringify({
            company_code_id: Number(form.company_code_id),
            title: form.title,
            description: form.description,
            due_date: form.due_date || null,
            reminder_days: Number(form.reminder_days || 0),
            urgency: form.urgency,
            source_type: form.source_type,
            source_id: form.source_id,
            source_label: form.source_label,
          }),
        }
      );

      const data = await response.json();

      if (!response.ok) {
        throw new Error(
          data?.error || "Actie wijzigen mislukt."
        );
      }

      showMessage("Opgeslagen ✅");
      clearRightColumn();
      await loadItems(view);
      dispatchActionCenterChanged();
    } catch (err) {
      setError(err?.message || String(err));
    }
  }

  async function deleteAction() {
    if (mode !== "detail" || !form.id) return;

    const actionType = normalizeText(form.action_type);

    if (actionType && actionType !== "manual") {
      setError(
        "Alleen handmatig aangemaakte acties kunnen worden verwijderd."
      );
      return;
    }

    const ok = window.confirm(
      `Actie "${form.title || ""}" definitief verwijderen?`
    );

    if (!ok) return;

    setError("");

    try {
      const response = await fetch(
        `/api/action-center/items/${encodeURIComponent(form.id)}`,
        {
          method: "DELETE",
          headers: getHeaders(),
          body: JSON.stringify({}),
        }
      );

      const data = await response.json().catch(() => ({}));

      if (!response.ok) {
        throw new Error(
          data?.error || "Actie verwijderen mislukt."
        );
      }

      showMessage("Verwijderd ✅");
      clearRightColumn();
      await loadItems(view);
      dispatchActionCenterChanged();
    } catch (err) {
      setError(err?.message || String(err));
    }
  }

  async function postponeAction(item) {
    const defaultDate = String(
      item?.postponed_until || item?.due_date || ""
    ).slice(0, 10);

    const value = window.prompt(
      "Uitstellen tot (JJJJ-MM-DD):",
      defaultDate
    );

    if (!value) return;

    setError("");

    try {
      const response = await fetch(
        `/api/action-center/items/${encodeURIComponent(item.id)}/postpone`,
        {
          method: "POST",
          headers: getHeaders(),
          body: JSON.stringify({
            postponed_until: value,
          }),
        }
      );

      const data = await response.json();

      if (!response.ok) {
        throw new Error(
          data?.error || "Uitstellen mislukt."
        );
      }

      showMessage(
        `Uitgesteld tot ${formatDateNL(value)} ✅`
      );

      clearRightColumn();
      await loadItems(view);
      dispatchActionCenterChanged();
    } catch (err) {
      setError(err?.message || String(err));
    }
  }

  async function completeAction(item) {
    const ok = window.confirm(
      `Actie "${item?.title || ""}" afhandelen?`
    );

    if (!ok) return;

    setError("");

    try {
      const response = await fetch(
        `/api/action-center/items/${encodeURIComponent(item.id)}/complete`,
        {
          method: "POST",
          headers: getHeaders(),
          body: JSON.stringify({
            retention_choice: "3_months",
          }),
        }
      );

      const data = await response.json();

      if (!response.ok) {
        throw new Error(
          data?.error || "Afhandelen mislukt."
        );
      }

      showMessage("Afgehandeld ✅");

      clearRightColumn();
      await loadItems(view);
      dispatchActionCenterChanged();
    } catch (err) {
      setError(err?.message || String(err));
    }
  }

  async function vernieuwList() {
    setMessage("");
    setError("");
    setQ("");
    setUrgencyFilter("");
    setDeadlineFilter("");
    setCompanyCodeFilter("");
    setSourceFilter("");
    setActionFilter("");
    setDocsFilter("");
    setStatusFilter("");
    setRelationFilter("");
    setPersonnelFilter("");
    setSort({ key: "deadline", dir: "asc" });
    clearRightColumn();
    await loadItems(view);
  }

  /* ============================================================
     SCHERM
     ============================================================ */

  const formIsEditable = mode === "new" || mode === "detail";
  const isFormLocked = mode === "search";
  const selectedItem = mode === "detail" ? form : null;

  return (
    <div>
      <div
        style={{
          display: "grid",
          gridTemplateColumns: isMobile
            ? "1fr"
            : `minmax(0, 1fr) ${RIGHT_WIDTH}px`,
          columnGap: GAP,
          rowGap: isMobile ? 22 : 0,
          alignItems: "start",
          minWidth: 0,
        }}
      >
        {/* =====================================================
            LINKERZIJDE: TITEL, WERKBALK EN GRID
            ===================================================== */}
        <div style={{ minWidth: 0 }}>
          <div
            style={{
              display: "flex",
              alignItems: "center",
              gap: 8,
              marginTop: 0,
              marginBottom: 10,
            }}
          >
            <h1 style={{ margin: 0 }}>
              Actiecentrum <span style={{ fontSize: 12, opacity: 0.5 }}>{VERSION}</span>
            </h1>

            <HelpButton
              topic="actiecentrum"
              title="Help over Actiecentrum"
            />
          </div>

          {!isMobile ? (
            <div
              style={{
                display: "flex",
                gap: 8,
                alignItems: "center",
                flexWrap: "nowrap",
              }}
            >
              <input
                ref={searchRef}
                value={q}
                onChange={(event) => {
                  setQ(event.target.value);
                  setMode("search");
                  setSelectedId(null);
                  setForm(emptyForm(""));
                  setDescriptionEditor(false);
                }}
                placeholder="Zoek..."
                style={searchInputStyle}
              />

              <div
                title={`Aantal regels in het grid: ${gridCount}`}
                style={{
                  minWidth: 52,
                  height: 32,
                  padding: "0 10px",
                  border: "1px solid #d9d9d9",
                  borderRadius: 4,
                  background: "#f7fafc",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  fontSize: 13,
                  fontWeight: 700,
                  color: "#1f4e79",
                  boxSizing: "border-box",
                  whiteSpace: "nowrap",
                }}
              >
                {gridCount}
              </div>

              <button
                type="button"
                onMouseDown={(event) => event.preventDefault()}
                onTouchStart={(event) => event.preventDefault()}
                onClick={() => voice.startOnce?.()}
                disabled={!voice.supported || voice.listening}
                style={!voice.supported || voice.listening ? buttonDisabledStyle : buttonStyle}
              >
                {voice.listening ? "🎤 Luistert..." : "🎤 Spreek"}
              </button>

              <button
                type="button"
                onClick={newItem}
                style={buttonStyle}
              >
                ➕ Nieuw
              </button>

              <button
                type="button"
                onClick={vernieuwList}
                style={buttonStyle}
              >
                🔄 Vernieuw
              </button>
            </div>
          ) : (
            <>
              <div
                style={{
                  display: "grid",
                  gridTemplateColumns: "minmax(0, 1fr) 58px",
                  gap: 6,
                  marginBottom: 8,
                  alignItems: "center",
                }}
              >
                <input
                  ref={searchRef}
                  value={q}
                  onChange={(event) => {
                    setQ(event.target.value);
                    setMode("search");
                    setSelectedId(null);
                    setForm(emptyForm(""));
                    setDescriptionEditor(false);
                  }}
                  placeholder="Zoek..."
                  style={{
                    ...searchInputStyle,
                    width: "100%",
                  }}
                />

                <div
                  style={{
                    height: 34,
                    border: "1px solid #d9d9d9",
                    borderRadius: 4,
                    background: "#f7fafc",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    fontSize: 13,
                    fontWeight: 700,
                    color: "#1f4e79",
                  }}
                >
                  {gridCount}
                </div>
              </div>

              <div
                style={{
                  display: "grid",
                  gridTemplateColumns: "repeat(3, minmax(0, 1fr))",
                  gap: 6,
                }}
              >
                <button
                  type="button"
                  onClick={() => voice.startOnce?.()}
                  disabled={!voice.supported || voice.listening}
                  style={!voice.supported || voice.listening ? buttonDisabledStyle : buttonStyle}
                >
                  {voice.listening ? "🎤..." : "🎤 Spreek"}
                </button>

                <button
                  type="button"
                  onClick={newItem}
                  style={buttonStyle}
                >
                  ➕ Nieuw
                </button>

                <button
                  type="button"
                  onClick={vernieuwList}
                  style={buttonStyle}
                >
                  🔄 Vernieuw
                </button>
              </div>
            </>
          )}

          <div
            style={{
              display: "flex",
              gap: 8,
              margin: "12px 0 10px 0",
              flexWrap: "wrap",
            }}
          >
            <button
              type="button"
              onClick={() => chooseView("open")}
              style={tabStyle(view === "open")}
            >
              Open
            </button>

            <button
              type="button"
              onClick={() => chooseView("postponed")}
              style={tabStyle(view === "postponed")}
            >
              Uitgesteld
            </button>

            <button
              type="button"
              onClick={() => chooseView("completed")}
              style={tabStyle(view === "completed")}
            >
              Afgehandeld
            </button>
          </div>

          {message && (
            <div
              style={{
                marginBottom: 8,
                color: "green",
              }}
            >
              {message}
            </div>
          )}

          {error && (
            <div
              style={{
                marginBottom: 8,
                color: "#b00020",
              }}
            >
              {error}
            </div>
          )}

          {speechInfo && <div style={{ opacity: 0.75 }}>{speechInfo}</div>}
          {voice.error && voice.error !== "no-speech" && (
            <div style={{ color: "crimson" }}>Spraakfout: {voice.error}</div>
          )}

          <div
            style={{
              overflowX: "scroll",
              overflowY: "auto",
              maxHeight: `calc(100vh - ${MID_BOTTOM_OFFSET}px)`,
              border: "1px solid #f0f0f0",
              borderRadius: 6,
              background: "#fff",
            }}
          >
            <div style={{ minWidth: TABLE_MIN_WIDTH }}>
              <table
                style={{
                  width: "100%",
                  borderCollapse: "separate",
                  borderSpacing: 0,
                  tableLayout: "fixed",
                }}
              >
                <colgroup>
                  <col style={{ width: 90 }} />
                  <col style={{ width: 110 }} />
                  <col style={{ width: 160 }} />
                  <col style={{ width: 250 }} />
                  <col style={{ width: 105 }} />
                  <col style={{ width: 65 }} />
                  <col style={{ width: 100 }} />
                  <col style={{ width: 180 }} />
                  <col style={{ width: 180 }} />
                </colgroup>

                <thead>
                  <tr>
                    <th style={thClickable} onClick={() => sortToggle("urgency")}>Urgentie{sortArrow("urgency")}</th>
                    <th style={thClickable} onClick={() => sortToggle("deadline")}>Deadline{sortArrow("deadline")}</th>
                    <th style={thClickable} onClick={() => sortToggle("source")}>Bron{sortArrow("source")}</th>
                    <th style={thClickable} onClick={() => sortToggle("action")}>Actie{sortArrow("action")}</th>
                    <th style={thClickable} onClick={() => sortToggle("company_code")}>Bedrijfscode{sortArrow("company_code")}</th>
                    <th style={thClickable} onClick={() => sortToggle("docs")}>Docs{sortArrow("docs")}</th>
                    <th style={thClickable} onClick={() => sortToggle("status")}>Status{sortArrow("status")}</th>
                    <th style={thClickable} onClick={() => sortToggle("relation")}>Relatie{sortArrow("relation")}</th>
                    <th style={thClickable} onClick={() => sortToggle("personnel")}>Personeel{sortArrow("personnel")}</th>
                  </tr>

                  <tr>
                    <th style={filterThStyle}>
                      <select value={urgencyFilter} onChange={(event) => setUrgencyFilter(event.target.value)} style={selectStyle}>
                        <option value="">(alle)</option>
                        {urgencyOptions.map((value) => <option key={value} value={value}>{value}</option>)}
                      </select>
                    </th>
                    <th style={filterThStyle}>
                      <select value={deadlineFilter} onChange={(event) => setDeadlineFilter(event.target.value)} style={selectStyle}>
                        <option value="">(alle)</option>
                        {deadlineOptions.map((value) => <option key={value} value={value}>{value}</option>)}
                      </select>
                    </th>
                    <th style={filterThStyle}>
                      <select value={sourceFilter} onChange={(event) => setSourceFilter(event.target.value)} style={selectStyle}>
                        <option value="">(alle)</option>
                        {sourceOptionsGrid.map((value) => <option key={value} value={value}>{value}</option>)}
                      </select>
                    </th>
                    <th style={filterThStyle}>
                      <select value={actionFilter} onChange={(event) => setActionFilter(event.target.value)} style={selectStyle}>
                        <option value="">(alle)</option>
                        {actionOptions.map((value) => <option key={value} value={value}>{value}</option>)}
                      </select>
                    </th>
                    <th style={filterThStyle}>
                      <select value={companyCodeFilter} onChange={(event) => setCompanyCodeFilter(event.target.value)} style={selectStyle}>
                        <option value="">(alle)</option>
                        {companyCodeOptions.map((value) => <option key={value} value={value}>{value}</option>)}
                      </select>
                    </th>
                    <th style={filterThStyle}>
                      <select value={docsFilter} onChange={(event) => setDocsFilter(event.target.value)} style={selectStyle}>
                        <option value="">(alle)</option>
                        {docsOptions.map((value) => <option key={value} value={value}>{value}</option>)}
                      </select>
                    </th>
                    <th style={filterThStyle}>
                      <select value={statusFilter} onChange={(event) => setStatusFilter(event.target.value)} style={selectStyle}>
                        <option value="">(alle)</option>
                        {statusOptions.map((value) => <option key={value} value={value}>{value}</option>)}
                      </select>
                    </th>
                    <th style={filterThStyle}>
                      <select value={relationFilter} onChange={(event) => setRelationFilter(event.target.value)} style={selectStyle}>
                        <option value="">(alle)</option>
                        {relationOptions.map((value) => <option key={value} value={value}>{value}</option>)}
                      </select>
                    </th>
                    <th style={filterThStyle}>
                      <select value={personnelFilter} onChange={(event) => setPersonnelFilter(event.target.value)} style={selectStyle}>
                        <option value="">(alle)</option>
                        {personnelOptions.map((value) => <option key={value} value={value}>{value}</option>)}
                      </select>
                    </th>
                  </tr>
                </thead>

                <tbody>
                  {sortedShown.map((item) => {
                    const isSelected =
                      String(selectedId || "") ===
                      String(item?.id || "");

                    return (
                      <tr
                        key={item.id}
                        onClick={() => selectItem(item)}
                        style={{
                          cursor: "pointer",
                          backgroundColor: isSelected
                            ? "#e6f0ff"
                            : "transparent",
                        }}
                      >
                        <td style={tdStyle}>
                          {urgencyLabel(item.urgency)}
                        </td>

                        <td style={tdStyle}>
                          {formatDateNL(item.due_date) || "-"}
                        </td>

                        <td style={tdStyle} title={item.source_label || ""}>
                          {item.source_label || "-"}
                        </td>

                        <td style={tdStyle} title={item.title || ""}>
                          {canOpenLinkedSource(item) ? (
                            <button
                              type="button"
                              onClick={(event) => {
                                event.stopPropagation();
                                openLinkedSource(item);
                              }}
                              style={{
                                border: "none",
                                background: "transparent",
                                padding: 0,
                                margin: 0,
                                color: "#1f4e79",
                                textDecoration: "underline",
                                cursor: "pointer",
                                font: "inherit",
                                maxWidth: "100%",
                                overflow: "hidden",
                                textOverflow: "ellipsis",
                                whiteSpace: "nowrap",
                                textAlign: "left",
                              }}
                              title="Open gekoppelde bron"
                            >
                              {item.title || "-"}
                            </button>
                          ) : (
                            item.title || "-"
                          )}
                        </td>

                        <td style={tdStyle} title={item.company_code_name || item.company_code || ""}>
                          {item.company_code_name || item.company_code || "-"}
                        </td>

                        <td style={tdStyle}>
                          {item.document_count || 0}
                        </td>

                        <td style={tdStyle} title={statusLabel(item)}>
                          {statusLabel(item)}
                        </td>

                        <td style={tdStyle} title={item.relation_name || ""}>
                          {item.relation_name || "-"}
                        </td>

                        <td style={tdStyle} title={item.personnel_name || ""}>
                          {item.personnel_name || "-"}
                        </td>
                      </tr>
                    );
                  })}

                  {!busy && sortedShown.length === 0 && (
                    <tr>
                      <td
                        colSpan={9}
                        style={{
                          padding: 12,
                          opacity: 0.7,
                        }}
                      >
                        Geen actiepunten gevonden.
                      </td>
                    </tr>
                  )}

                  {busy && (
                    <tr>
                      <td
                        colSpan={9}
                        style={{
                          padding: 12,
                          opacity: 0.7,
                        }}
                      >
                        Laden...
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>
        </div>

        {/* =====================================================
            RECHTERZIJDE: STANDAARD STAFFIQ INVOERKOLOM
            ===================================================== */}
        <div
          ref={rightScrollRef}
          style={{
            width: isMobile ? "100%" : RIGHT_WIDTH,
            minWidth: isMobile ? 0 : RIGHT_WIDTH,
            maxWidth: isMobile ? "100%" : RIGHT_WIDTH,
            position: isMobile ? "relative" : "sticky",
            top: isMobile ? "auto" : 0,
            alignSelf: "start",
            maxHeight: isMobile
              ? "none"
              : "calc(100vh - 40px)",
            overflowY: isMobile ? "visible" : "auto",
            background:
              "linear-gradient(180deg, #0b4f7a 0%, #083b5c 100%)",
            borderRadius: 10,
            padding: 10,
            paddingBottom: isMobile ? 34 : 10,
            boxSizing: "border-box",
            marginTop: isMobile ? 6 : 0,
            marginBottom: isMobile ? 34 : 0,
          }}
        >
          <div
            style={{
              background: "white",
              borderRadius: 10,
              padding: 12,
              paddingBottom: isMobile ? 28 : 12,
              boxSizing: "border-box",
              width: "100%",
              maxWidth: "100%",
              minWidth: 0,
              overflow: isMobile ? "visible" : "hidden",
            }}
          >
            <div
              style={{
                position: "relative",
                minHeight: descriptionEditor ? 42 : 42,
                marginBottom: 4,
                boxSizing: "border-box",
              }}
            >
              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                  gap: 8,
                }}
              >
                <h3 style={{ marginTop: 0, marginBottom: 0 }}>
                  Actiecentrum
                </h3>

                {descriptionEditor && (
                  <button
                    type="button"
                    onClick={closeDescriptionEditorToSmall}
                    style={buttonStyle}
                    title="Terug naar normaal formulier"
                  >
                    ↩ Terug
                  </button>
                )}
              </div>
            </div>

            {descriptionEditor ? (
              <div style={{ display: "grid", gap: 10 }}>
                <textarea
                  value={form.description || ""}
                  disabled={isFormLocked}
                  onChange={(event) =>
                    setField("description", event.target.value)
                  }
                  rows={15}
                  style={{
                    padding: "10px 10px",
                    width: "100%",
                    border: "1px solid #d9d9d9",
                    borderRadius: 6,
                    background: "white",
                    resize: "vertical",
                    boxSizing: "border-box",
                    minHeight: "60vh",
                    fontSize: 14,
                    lineHeight: 1.4,
                  }}
                />

                <div
                  style={{
                    background: "white",
                    paddingTop: 10,
                    marginTop: 6,
                    display: "grid",
                    gridTemplateColumns: "1fr 1fr",
                    gap: 8,
                    borderTop: "1px solid #eee",
                  }}
                >
                  <button
                    type="button"
                    onClick={() => {
                      if (mode === "new") createAction();
                      if (mode === "detail") updateAction();
                    }}
                    disabled={isFormLocked}
                    style={processButtonStyle(
                      primaryButtonStyle,
                      isFormLocked
                    )}
                  >
                    💾 Opslaan
                  </button>

                  <button
                    type="button"
                    onClick={closeDescriptionEditorToSmall}
                    style={processButtonStyle(buttonStyle, false)}
                  >
                    ↩ Terug
                  </button>
                </div>
              </div>
            ) : (
              <div
                style={{
                  display: "grid",
                  gridTemplateColumns: "minmax(0, 1fr)",
                  gap: 8,
                  width: "100%",
                  maxWidth: "100%",
                  minWidth: 0,
                  boxSizing: "border-box",
                  overflow: "hidden",
                  justifyItems: "stretch",
                  alignItems: "start",
                }}
              >
                <div style={sectionTitleStyle}>Actie</div>

                <Field label="Bedrijfscode">
                  <select
                    value={form.company_code_id || ""}
                    disabled={isFormLocked}
                    onChange={(event) =>
                      setField("company_code_id", event.target.value)
                    }
                    style={selectStyle}
                  >
                    <option value="">(geen)</option>
                    {codes.map((code) => (
                      <option key={code.id} value={code.id}>
                        {code.name || code.code}
                      </option>
                    ))}
                  </select>
                </Field>

                <Field label="Titel">
                  <input
                    value={form.title || ""}
                    disabled={isFormLocked}
                    onChange={(event) =>
                      setField("title", event.target.value)
                    }
                    style={inputStyle}
                  />
                </Field>

                <Field label="Omschrijving">
                  <textarea
                    value={form.description || ""}
                    disabled={isFormLocked}
                    onChange={(event) =>
                      setField("description", event.target.value)
                    }
                    rows={6}
                    style={{ ...inputStyle, resize: "vertical" }}
                    onFocus={() => {
                      if (!isFormLocked) {
                        openDescriptionEditor();
                      }
                    }}
                  />
                </Field>

                <div
                  style={{
                    display: "grid",
                    gridTemplateColumns: "1fr 1fr",
                    gap: 8,
                  }}
                >
                  <div>
                    <label style={labelStyle}>Deadline</label>
                    <input
                      type="date"
                      value={form.due_date || ""}
                      disabled={isFormLocked}
                      onChange={(event) =>
                        setField("due_date", event.target.value)
                      }
                      style={inputStyle}
                    />
                  </div>

                  <div>
                    <label style={labelStyle}>Herinnering dagen</label>
                    <input
                      type="number"
                      min="0"
                      value={form.reminder_days ?? ""}
                      disabled={isFormLocked}
                      onChange={(event) =>
                        setField("reminder_days", event.target.value)
                      }
                      style={inputStyle}
                    />
                  </div>
                </div>

                <Field label="Urgentie">
                  <select
                    value={form.urgency || ""}
                    disabled={isFormLocked}
                    onChange={(event) =>
                      setField("urgency", event.target.value)
                    }
                    style={selectStyle}
                  >
                    <option value="">(geen)</option>
                    <option value="info">Info</option>
                    <option value="normaal">Normaal</option>
                    <option value="hoog">Hoog</option>
                    <option value="spoed">Spoed</option>
                  </select>
                </Field>

                <hr style={{ margin: "10px 0" }} />

                <div style={sectionTitleStyle}>Bronkoppeling</div>

                <Field label="Soort bron">
                  <select
                    value={form.source_type || ""}
                    disabled={isFormLocked}
                    onChange={(event) => {
                      const nextType = event.target.value;
                      setForm((current) => ({
                        ...current,
                        source_type: nextType,
                        source_id: "",
                        source_label: "",
                      }));
                    }}
                    style={selectStyle}
                  >
                    <option value="">(geen)</option>
                    <option value="organisation">Relatie</option>
                    <option value="personnel">Personeel</option>
                    <option value="application">Aanvraag</option>
                    <option value="vacancy">Vacature</option>
                    <option value="placement">Plaatsing</option>
                    <option value="offer">Offerte/Opdracht</option>
                    <option value="invoice">Factuur</option>
                    <option value="note_task">Notitie/Taak</option>
                    <option value="other">Overig</option>
                    {form.source_type &&
                      ![
                        "organisation",
                        "personnel",
                        "application",
                        "vacancy",
                        "placement",
                        "offer",
                        "invoice",
                        "note_task",
                        "other",
                      ].includes(normalizeText(form.source_type)) && (
                        <option value={form.source_type}>
                          {sourceTypeLabel(form.source_type) || form.source_type}
                        </option>
                      )}
                  </select>
                </Field>

                {[
                  "organisation",
                  "personnel",
                  "application",
                  "vacancy",
                  "placement",
                  "offer",
                  "invoice",
                  "note_task",
                ].includes(normalizeText(form.source_type)) ? (
                  <Field label="Bron">
                    <select
                      value={form.source_id || ""}
                      disabled={isFormLocked || sourceBusy}
                      onChange={(event) => {
                        const sourceId = event.target.value;
                        const selected = sourceOptions.find(
                          (item) => String(item.id) === String(sourceId)
                        );

                        setForm((current) => ({
                          ...current,
                          source_id: sourceId,
                          source_label: selected?.label || "",
                        }));
                      }}
                      style={selectStyle}
                    >
                      <option value="">
                        {sourceBusy ? "Bronnen laden..." : "(geen)"}
                      </option>
                      {form.source_id &&
                        form.source_label &&
                        !sourceOptions.some(
                          (item) => String(item.id) === String(form.source_id)
                        ) && (
                          <option value={form.source_id}>
                            {form.source_label}
                          </option>
                        )}
                      {sourceOptions.map((item) => (
                        <option key={item.id} value={item.id}>
                          {item.label}
                        </option>
                      ))}
                    </select>
                  </Field>
                ) : normalizeText(form.source_type) === "other" ? (
                  <Field label="Omschrijving bron">
                    <input
                      value={form.source_label || ""}
                      disabled={isFormLocked}
                      onChange={(event) => {
                        const value = event.target.value;
                        setForm((current) => ({
                          ...current,
                          source_id: "",
                          source_label: value,
                        }));
                      }}
                      style={inputStyle}
                    />
                  </Field>
                ) : null}

                <hr style={{ margin: "10px 0" }} />

                <div style={sectionTitleStyle}>Status / opvolging</div>

                <Field label="Status">
                  <input
                    value={mode === "detail" ? statusLabel(form) : ""}
                    disabled
                    style={inputStyle}
                  />
                </Field>

                <Field label="Documenten">
                  <input
                    value={mode === "detail" ? form.document_count || 0 : ""}
                    disabled
                    style={inputStyle}
                  />
                </Field>

                <div
                  style={{
                    display: "grid",
                    gridTemplateColumns: "1fr 1fr",
                    gap: 8,
                    marginTop: 8,
                    width: "100%",
                    boxSizing: "border-box",
                  }}
                >
                  {mode === "new" ? (
                    <>
                      <button
                        type="button"
                        onClick={createAction}
                        style={processButtonStyle(
                          primaryButtonStyle,
                          false
                        )}
                      >
                        💾 Opslaan
                      </button>

                      <button
                        type="button"
                        onClick={clearRightColumn}
                        style={processButtonStyle(buttonStyle, false)}
                      >
                        🔄 Vernieuw
                      </button>
                    </>
                  ) : mode === "detail" ? (
                    <>
                      <button
                        type="button"
                        onClick={() =>
                          postponeAction(selectedItem)
                        }
                        disabled={
                          normalizeText(form.status) !== "open"
                        }
                        style={processButtonStyle(
                          neutralButtonStyle,
                          normalizeText(form.status) !== "open"
                        )}
                      >
                        Uitstellen
                      </button>

                      <button
                        type="button"
                        onClick={() =>
                          completeAction(selectedItem)
                        }
                        disabled={
                          normalizeText(form.status) !== "open"
                        }
                        style={processButtonStyle(
                          successButtonStyle,
                          normalizeText(form.status) !== "open"
                        )}
                      >
                        Afhandelen
                      </button>

                      <button
                        type="button"
                        onClick={updateAction}
                        style={processButtonStyle(
                          primaryButtonStyle,
                          false
                        )}
                      >
                        💾 Opslaan
                      </button>

                      <button
                        type="button"
                        onClick={deleteAction}
                        disabled={
                          normalizeText(form.action_type || "manual") !==
                          "manual"
                        }
                        style={processButtonStyle(
                          dangerButtonStyle,
                          normalizeText(form.action_type || "manual") !==
                            "manual"
                        )}
                      >
                        🗑 Verwijderen
                      </button>

                      <button
                        type="button"
                        onClick={clearRightColumn}
                        style={processButtonStyle(buttonStyle, false)}
                      >
                        🔄 Vernieuw
                      </button>
                    </>
                  ) : (
                    <>
                      <button
                        type="button"
                        disabled
                        style={processButtonStyle(
                          primaryButtonStyle,
                          true
                        )}
                      >
                        💾 Opslaan
                      </button>

                      <button
                        type="button"
                        onClick={vernieuwList}
                        style={processButtonStyle(buttonStyle, false)}
                      >
                        🔄 Vernieuw
                      </button>
                    </>
                  )}
                </div>
              </div>
            )}
          </div>
        </div>
      </div>

    </div>
  );
}
